"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.diluteBatch = exports.topUpBatch = exports.createPostageBatch = exports.getPostageBatchBuckets = exports.getPostageBatch = exports.getAllPostageBatches = exports.getGlobalPostageBatches = void 0;
const cafe_utility_1 = require("cafe-utility");
const duration_1 = require("../../utils/duration");
const http_1 = require("../../utils/http");
const size_1 = require("../../utils/size");
const stamps_1 = require("../../utils/stamps");
const type_1 = require("../../utils/type");
const typed_bytes_1 = require("../../utils/typed-bytes");
const workaround_1 = require("../../utils/workaround");
const STAMPS_ENDPOINT = 'stamps';
const BATCHES_ENDPOINT = 'batches';
async function getGlobalPostageBatches(requestOptions) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'get',
        url: `${BATCHES_ENDPOINT}`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    const batches = cafe_utility_1.Types.asArray(body.batches, { name: 'batches' }).map(x => cafe_utility_1.Types.asObject(x, { name: 'batch' }));
    return batches.map(x => ({
        batchID: new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(x.batchID, { name: 'batchID' })),
        batchTTL: cafe_utility_1.Types.asNumber(x.batchTTL, { name: 'batchTTL' }),
        bucketDepth: cafe_utility_1.Types.asNumber(x.bucketDepth, { name: 'bucketDepth' }),
        depth: cafe_utility_1.Types.asNumber(x.depth, { name: 'depth' }),
        immutable: cafe_utility_1.Types.asBoolean(x.immutable, { name: 'immutable' }),
        owner: new typed_bytes_1.EthAddress(cafe_utility_1.Types.asString(x.owner, { name: 'owner' })),
        start: cafe_utility_1.Types.asNumber(x.start, { name: 'start' }),
        value: (0, type_1.asNumberString)(x.value, { name: 'value' }),
    }));
}
exports.getGlobalPostageBatches = getGlobalPostageBatches;
async function getAllPostageBatches(requestOptions) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'get',
        url: `${STAMPS_ENDPOINT}`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    const stamps = cafe_utility_1.Types.asArray(body.stamps, { name: 'stamps' }).map(x => cafe_utility_1.Types.asObject(x, { name: 'stamp' }));
    return stamps.map(x => {
        const utilization = cafe_utility_1.Types.asNumber(x.utilization, { name: 'utilization' });
        const depth = cafe_utility_1.Types.asNumber(x.depth, { name: 'depth' });
        const bucketDepth = cafe_utility_1.Types.asNumber(x.bucketDepth, { name: 'bucketDepth' });
        const usage = (0, stamps_1.getStampUsage)(utilization, depth, bucketDepth);
        const batchTTL = (0, workaround_1.normalizeBatchTTL)(cafe_utility_1.Types.asNumber(x.batchTTL, { name: 'batchTTL' }));
        const duration = duration_1.Duration.fromSeconds(batchTTL);
        const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(depth);
        return {
            batchID: new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(x.batchID, { name: 'batchID' })),
            utilization,
            usable: cafe_utility_1.Types.asBoolean(x.usable, { name: 'usable' }),
            label: cafe_utility_1.Types.asEmptiableString(x.label, { name: 'label' }),
            depth,
            amount: (0, type_1.asNumberString)(x.amount, { name: 'amount' }),
            bucketDepth,
            blockNumber: cafe_utility_1.Types.asNumber(x.blockNumber, { name: 'blockNumber' }),
            immutableFlag: cafe_utility_1.Types.asBoolean(x.immutableFlag, { name: 'immutableFlag' }),
            usage,
            usageText: `${Math.round(usage * 100)}%`,
            size: size_1.Size.fromBytes(effectiveBytes),
            remainingSize: size_1.Size.fromBytes(Math.ceil(effectiveBytes * (1 - usage))),
            theoreticalSize: size_1.Size.fromBytes((0, stamps_1.getStampTheoreticalBytes)(depth)),
            duration,
            calculateSize(encryption, redundancyLevel) {
                const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(this.depth, encryption, redundancyLevel);
                return size_1.Size.fromBytes(effectiveBytes);
            },
            calculateRemainingSize(encryption, redundancyLevel) {
                const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(this.depth, encryption, redundancyLevel);
                return size_1.Size.fromBytes(Math.ceil(effectiveBytes * (1 - this.usage)));
            },
        };
    });
}
exports.getAllPostageBatches = getAllPostageBatches;
async function getPostageBatch(requestOptions, postageBatchId, encryption, erasureCodeLevel) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'get',
        url: `${STAMPS_ENDPOINT}/${postageBatchId}`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    const utilization = cafe_utility_1.Types.asNumber(body.utilization, { name: 'utilization' });
    const depth = cafe_utility_1.Types.asNumber(body.depth, { name: 'depth' });
    const bucketDepth = cafe_utility_1.Types.asNumber(body.bucketDepth, { name: 'bucketDepth' });
    const usage = (0, stamps_1.getStampUsage)(utilization, depth, bucketDepth);
    const batchTTL = (0, workaround_1.normalizeBatchTTL)(cafe_utility_1.Types.asNumber(body.batchTTL, { name: 'batchTTL' }));
    const duration = duration_1.Duration.fromSeconds(batchTTL);
    const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(depth, encryption, erasureCodeLevel);
    return {
        batchID: new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(body.batchID, { name: 'batchID' })),
        utilization,
        usable: cafe_utility_1.Types.asBoolean(body.usable, { name: 'usable' }),
        label: cafe_utility_1.Types.asEmptiableString(body.label, { name: 'label' }),
        depth,
        amount: (0, type_1.asNumberString)(body.amount, { name: 'amount' }),
        bucketDepth,
        blockNumber: cafe_utility_1.Types.asNumber(body.blockNumber, { name: 'blockNumber' }),
        immutableFlag: cafe_utility_1.Types.asBoolean(body.immutableFlag, { name: 'immutableFlag' }),
        usage,
        usageText: `${Math.round(usage * 100)}%`,
        size: size_1.Size.fromBytes(effectiveBytes),
        remainingSize: size_1.Size.fromBytes(Math.ceil(effectiveBytes * (1 - usage))),
        theoreticalSize: size_1.Size.fromBytes((0, stamps_1.getStampTheoreticalBytes)(depth)),
        duration,
        calculateSize(encryption, redundancyLevel) {
            const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(depth, encryption, redundancyLevel);
            return size_1.Size.fromBytes(effectiveBytes);
        },
        calculateRemainingSize(encryption, redundancyLevel) {
            const effectiveBytes = (0, stamps_1.getStampEffectiveBytes)(depth, encryption, redundancyLevel);
            return size_1.Size.fromBytes(Math.ceil(effectiveBytes * (1 - usage)));
        },
    };
}
exports.getPostageBatch = getPostageBatch;
async function getPostageBatchBuckets(requestOptions, postageBatchId) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'get',
        url: `${STAMPS_ENDPOINT}/${postageBatchId}/buckets`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    return {
        depth: cafe_utility_1.Types.asNumber(body.depth, { name: 'depth' }),
        bucketDepth: cafe_utility_1.Types.asNumber(body.bucketDepth, { name: 'bucketDepth' }),
        bucketUpperBound: cafe_utility_1.Types.asNumber(body.bucketUpperBound, { name: 'bucketUpperBound' }),
        buckets: cafe_utility_1.Types.asArray(body.buckets, { name: 'buckets' })
            .map(x => cafe_utility_1.Types.asObject(x, { name: 'bucket' }))
            .map(x => ({
            bucketID: cafe_utility_1.Types.asNumber(x.bucketID, { name: 'bucketID' }),
            collisions: cafe_utility_1.Types.asNumber(x.collisions, { name: 'collisions' }),
        })),
    };
}
exports.getPostageBatchBuckets = getPostageBatchBuckets;
async function createPostageBatch(requestOptions, amount, depth, options) {
    const headers = {};
    if (options?.gasPrice) {
        headers['gas-price'] = options.gasPrice.toString();
    }
    if (options?.immutableFlag !== undefined) {
        headers.immutable = String(options.immutableFlag);
    }
    const response = await (0, http_1.http)(requestOptions, {
        method: 'post',
        url: `${STAMPS_ENDPOINT}/${amount}/${depth}`,
        responseType: 'json',
        params: { label: options?.label },
        headers,
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    return new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(body.batchID, { name: 'batchID' }));
}
exports.createPostageBatch = createPostageBatch;
async function topUpBatch(requestOptions, id, amount) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'patch',
        url: `${STAMPS_ENDPOINT}/topup/${id}/${amount}`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    return new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(body.batchID, { name: 'batchID' }));
}
exports.topUpBatch = topUpBatch;
async function diluteBatch(requestOptions, id, depth) {
    const response = await (0, http_1.http)(requestOptions, {
        method: 'patch',
        url: `${STAMPS_ENDPOINT}/dilute/${id}/${depth}`,
        responseType: 'json',
    });
    const body = cafe_utility_1.Types.asObject(response.data, { name: 'response.data' });
    return new typed_bytes_1.BatchId(cafe_utility_1.Types.asString(body.batchID, { name: 'batchID' }));
}
exports.diluteBatch = diluteBatch;
